// The primary reach of a hull (R2738, ui-map-interface.md §6.1): the one
// reach the map draws for the hull under the pointer.
//
// A warship and the Sentinel show the reach of their guns, a Tender the
// reach of its heal beam, and a Sapper the reach of COLLECT. The Beacon
// keeps its own range rings (card 46, R565), and the Vedette, the Oracle
// and the Dredger show no reach here. The module is pure: the host hands
// in the figures, which all come from the hello catalog, so the client
// mirrors no table.

import { FLEET_REACH_RING, MARKER_NEUTRAL } from '../../gen/presentation';
import { ShipClass } from '../../gen/proto/aquaphobia';

/** Which reach is the primary reach of a hull. */
export type PrimaryReach = 'gun' | 'beam' | 'collect' | 'none';

/** The classes that show no primary reach on a hover. */
const NO_REACH = new Set<ShipClass>([
  ShipClass.SHIP_CLASS_UNSPECIFIED,
  ShipClass.SHIP_CLASS_BEACON,
  ShipClass.SHIP_CLASS_VEDETTE,
  ShipClass.SHIP_CLASS_ORACLE,
  ShipClass.SHIP_CLASS_DREDGER,
]);

/** The primary reach of class `cls`. A class the viewer does not know is `none`. */
export function primaryReachOf(cls: ShipClass): PrimaryReach {
  if (cls === ShipClass.SHIP_CLASS_TENDER) return 'beam';
  if (cls === ShipClass.SHIP_CLASS_SAPPER) return 'collect';
  return NO_REACH.has(cls) ? 'none' : 'gun';
}

/**
 * The radius of a primary reach in world px, from the three figures of the
 * host: the gun reach of the hull, the heal beam reach of a Tender and the
 * collect reach of a Sapper. 0 draws no ring.
 */
export function primaryReachPx(kind: PrimaryReach, gun: number, beamPx: number, collectPx: number): number {
  switch (kind) {
    case 'gun': return gun;
    case 'beam': return beamPx;
    case 'collect': return collectPx;
    default: return 0;
  }
}

/**
 * The ink of a reach ring: the attack red for a gun (R533), the verdict
 * green of the heal beam for a beam, and the neutral white for a collect,
 * because a collect harms nothing (R2711). The selected ring and the
 * hovered ring of one reach wear one ink.
 */
export function reachInk(kind: PrimaryReach): number {
  if (kind === 'beam') return FLEET_REACH_RING.beamColor;
  if (kind === 'collect') return MARKER_NEUTRAL;
  return FLEET_REACH_RING.gunColor;
}
